import {
  createRateLimiter,
  isUnhandledErrorEvent,
  parseSampleRate,
} from '@griply/utils';
import * as Sentry from '@sentry/react';
import { version } from 'shared/constants';

// Browser noise exlcusion; not app code.
const browserNoiseIgnoreErrors = [
  /runtime\.sendMessage/i,
  /Extension context invalidated/i,
  /message port closed before a response was received/i,
  /WKWebView API client did not respond to this postMessage/i,
  // WebKit reports an extension's injected script with our own page as the source, so no URL can match it.
  /__firefox__/i,
];

const extensionDenyUrls = [
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari(-web)?-extension:\/\//i,
];

// Sentry attaches the page URL to every event; `sendDefaultPii: false` does not cover it.
const CREDENTIAL_PARAMETERS = ['oobCode', 'claimToken'];

export const redactCredentialParameters = (url: string): string => {
  if (
    !CREDENTIAL_PARAMETERS.some((parameter) => url.includes(`${parameter}=`))
  ) {
    return url;
  }

  return CREDENTIAL_PARAMETERS.reduce(
    (redacted, parameter) =>
      redacted.replace(
        new RegExp(`([?&]${parameter}=)[^&#]*`, 'gi'),
        '$1[redacted]',
      ),
    url,
  );
};

type RedactableEvent = {
  request?: { url?: string };
  breadcrumbs?: { data?: Record<string, unknown> }[];
};

const redactEvent = <T extends RedactableEvent>(event: T): T => {
  if (event.request?.url) {
    event.request.url = redactCredentialParameters(event.request.url);
  }

  event.breadcrumbs?.forEach((breadcrumb) => {
    if (typeof breadcrumb.data?.to === 'string') {
      breadcrumb.data.to = redactCredentialParameters(breadcrumb.data.to);
    }

    if (typeof breadcrumb.data?.from === 'string') {
      breadcrumb.data.from = redactCredentialParameters(breadcrumb.data.from);
    }
  });

  return event;
};

const rateLimit = createRateLimiter({
  itemsPerMinute: 10,
  maxItems: 20,
  shouldBypass: isUnhandledErrorEvent,
});

let initialized = false;

export const init = () => {
  if (initialized) {
    return;
  }
  initialized = true;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const enabled = environment !== 'local' && !!dsn;

  Sentry.init({
    dsn,
    enabled,
    environment,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    sampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_SAMPLE_RATE, 1.0),
    tracesSampleRate: parseSampleRate(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
      0.1,
    ),
    sendDefaultPii: false,
    ignoreErrors: browserNoiseIgnoreErrors,
    denyUrls: extensionDenyUrls,
    beforeSend: (event) => {
      const allowed = rateLimit(event);

      return allowed && redactEvent(allowed);
    },
    beforeSendTransaction: redactEvent,
    // Without this, non-standard error properties (Firebase's `code`, `customData`) never reach Sentry.
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.extraErrorDataIntegration(),
    ],
    _experiments: { enableLogs: true },
    initialScope: {
      tags: { appVersion: version },
    },
  });
};
